import React, { type FC, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { attachCopyEditB2B } from 'reactApp/appHelpers/featuresHelpers/features/attachCopyEditB2B';
import { shouldShowCloneButtonForCloudAttach } from 'reactApp/appHelpers/featuresHelpers/features/cloneButtonForCloudAttach';
import { editDocumentsInAttachesExts } from 'reactApp/appHelpers/featuresHelpers/features/editDocumentsInAttachesExts';
import { checkAllDocumentsUnlockRequest } from 'reactApp/modules/allDocuments/allDocuments.module';
import { getAllDocumentsAccessRequestSent } from 'reactApp/modules/allDocuments/allDocuments.selectors';
import { type AttachesItem, EAttachTypes } from 'reactApp/modules/attaches/attaches.types';
import { openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { PromoSelectors } from 'reactApp/modules/promo/promo.selectors';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { EditorRestrictionTooltipWrapper } from 'reactApp/ui/EditorRestriction/EditorRestrictionTooltip/EditorRestrictionTooltipWrapper';
import { sendViewerDwh } from 'reactApp/ui/ReactViewer/ReactViewer.helpers';
import { AttachMenuInfo } from 'reactApp/ui/ReactViewer/ViewerMenu/AttachMenuInfo/AttachMenuInfo';
import { CloneMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/CloneMenuButton/CloneMenuButton';
import { CloseMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/CloseMenuButton/CloseMenuButton';
import { DownloadMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/DownloadMenuButton/DownloadMenuButton';
import { EditDocumentCopyTooltip } from 'reactApp/ui/ReactViewer/ViewerMenu/EditDocumentCopyTooltip/EditDocumentCopyTooltip';
import { EditMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/EditMenuButton/EditMenuButton';
import { MenuLogo } from 'reactApp/ui/ReactViewer/ViewerMenu/MenuLogo/MenuLogo';
import { MoreMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/MoreMenuButton/MoreMenuButton';
import { MenuItemName } from 'reactApp/ui/ReactViewer/ViewerMenu/MoreMenuButton/MoreMenuButton.types';
import { ShareMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/ShareMenuButton/ShareMenuButton';
import { useSummarizeMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/SummarizeMenuButton/SummarizeMenuButton';
import type { ViewerMenuProps } from 'reactApp/ui/ReactViewer/ViewerMenu/ViewerMenu';
import { ViewerMenuCenter, ViewerMenuEnd, ViewerMenuStart, ViewerMenuWrapper } from 'reactApp/ui/ReactViewer/ViewerMenu/ViewerMenu.blocks';
import { Zoomer } from 'reactApp/ui/ReactViewer/ViewerMenu/Zoomer/Zoomer';
import { canCloneWeblink } from 'reactApp/utils/cloneHelpers';

function showEditTooltipForExt(ext: string) {
    return editDocumentsInAttachesExts.includes(ext);
}

interface Props extends ViewerMenuProps {
    file: AttachesItem;
}

export const AttachMenu: FC<Props> = ({
    file,
    fileStorage,
    isAuthorized,
    showImageEdit,
    isArchiveItem,
    zoom,
    decreaseZoom,
    increaseZoom,
    onFileClick,
    onActive,
    showDownload,
    onDownload,
    onPublish,
    onClone,
    onImageEdit,
    showEdit,
    onAttachCopyEdit,
    showPdfEdit,
    onPdfAttachCopyEdit,
    allowPrinting,
    onPrint,
    showClose,
    onClose,
    isEditingDisabled,
}) => {
    const showClone = canCloneWeblink(isAuthorized, fileStorage);
    const isTemporary = file.attachType === EAttachTypes.temporary;
    const isCloudAttach = file.attachType === EAttachTypes.cloud;
    const isAllDocumentsRequestSent = useSelector(getAllDocumentsAccessRequestSent);
    const b2bBizUser = useSelector(UserSelectors.isB2BUser);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    // CLOUDWEB-19983 включаем возможность редактирования аттачей для всех юзеров по feature flag
    // при его отсутствии возможность редактирования сохраняется только для b2c
    const isCanEditAttach = !b2bBizUser || attachCopyEditB2B;
    const isFreeB2B = b2bBizUser && !isPaidUser;
    const showAttachEditButton = showEdit && !isTemporary && !isCloudAttach && isCanEditAttach;
    const showAttachCloneButton = showClone && !isArchiveItem && !isTemporary && (!isCloudAttach || shouldShowCloneButtonForCloudAttach);
    const showAttachShareButton = showClone && !isArchiveItem && !isTemporary && !isCloudAttach;
    const ext = file?.ext?.toLowerCase();
    const summarizeButton = useSummarizeMenuButton(ext);
    const dispatch = useDispatch();

    const editButtonRef = useRef<HTMLDivElement>(null);

    const handleEditClickForFreeB2B = () => {
        if (isAllDocumentsRequestSent) {
            dispatch(
                showSnackbarAction({
                    id: 'alldoc-request-has-sent',
                    type: SnackbarTypes.success,
                    title: 'Запрос отправлен',
                    text: 'Вам придет письмо, когда администратор подключит платный тариф',
                    closable: true,
                })
            );
        } else {
            openPopupHelper({
                popupName: popupNames.ALL_DOCUMENTS_REQUEST_MODAL,
            });
        }
    };

    const onEditButtonClick = () => {
        if (isEditingDisabled) {
            return;
        }
        if (isFreeB2B) {
            handleEditClickForFreeB2B();
        } else {
            onAttachCopyEdit(file);
        }
    };

    const { ext: extension } = file;
    const editDocumentsInAttachesTooltip = useSelector(PromoSelectors.getPromo(EPromoType.editDocumentsInAttachesTooltip));
    const showEditDocumentsInAttachesTooltip =
        editDocumentsInAttachesTooltip && showAttachEditButton && showEditTooltipForExt(extension) && !isEditingDisabled;

    useEffect(() => {
        if (!showEditDocumentsInAttachesTooltip) {
            if (isEditingDisabled) {
                editDocumentsInAttachesTooltip?.onEditDisabled();
            }

            return;
        }

        editDocumentsInAttachesTooltip?.onShow();
        sendViewerDwh({
            action: 'edit-promo-show',
            extension,
        });
    }, [showEditDocumentsInAttachesTooltip, isEditingDisabled, editDocumentsInAttachesTooltip, extension]);

    useEffect(() => {
        dispatch(checkAllDocumentsUnlockRequest());
    }, [isFreeB2B]);

    const moreItems = useMemo(
        () => [
            ...(allowPrinting
                ? [
                      {
                          name: MenuItemName.PRINT,
                          onClick: () => {
                              onPrint(file);
                          },
                      },
                  ]
                : []),
        ],
        [allowPrinting, onPrint]
    );

    return (
        <ViewerMenuWrapper>
            <ViewerMenuStart>
                <MenuLogo />
                <AttachMenuInfo file={file} onClick={onFileClick} onActive={onActive} />
            </ViewerMenuStart>
            <ViewerMenuCenter>{zoom && <Zoomer value={zoom} decrease={decreaseZoom} increase={increaseZoom} />}</ViewerMenuCenter>
            <ViewerMenuEnd>
                {summarizeButton}
                {showAttachShareButton && (
                    <>
                        <ShareMenuButton
                            onClick={() => {
                                onPublish(file);
                            }}
                        />
                        {showImageEdit && (
                            <EditMenuButton
                                onClick={() => {
                                    onImageEdit(file);
                                }}
                            />
                        )}
                    </>
                )}
                {showAttachEditButton && (
                    <EditorRestrictionTooltipWrapper isEditingDisabled={isEditingDisabled}>
                        <EditMenuButton
                            hint="Редактировать копию"
                            qaId="edit_public_copy"
                            onClick={onEditButtonClick}
                            ref={editButtonRef}
                            editCopy
                        />
                    </EditorRestrictionTooltipWrapper>
                )}
                {showEditDocumentsInAttachesTooltip && (
                    <EditDocumentCopyTooltip
                        target={editButtonRef}
                        onClick={onEditButtonClick}
                        onClose={() => {
                            editDocumentsInAttachesTooltip?.onClose();
                        }}
                        onCrossClose={() => {
                            editDocumentsInAttachesTooltip?.onCrossClose();
                        }}
                    />
                )}
                {showPdfEdit && (
                    <EditMenuButton
                        hint="Редактировать копию"
                        qaId="edit"
                        onClick={() => {
                            onPdfAttachCopyEdit(file);
                        }}
                    />
                )}
                {showAttachCloneButton && <CloneMenuButton onClick={onClone} />}
                {showDownload && (
                    <DownloadMenuButton
                        file={file}
                        onClick={() => {
                            onDownload(file);
                        }}
                    />
                )}
                <MoreMenuButton list={moreItems} />
                {showClose && (
                    <CloseMenuButton
                        onClick={() => {
                            onClose(file);
                        }}
                    />
                )}
            </ViewerMenuEnd>
        </ViewerMenuWrapper>
    );
};
